/*
 * @Author: Tahiti
 * @Date: 2025-10-27
 * @Description: 创作历史记录 API
 * @FilePath: /weryai-web/api/creations.ts
 */
import { http } from '~/utils/request'

// 音乐历史记录详情
export interface MusicHistoryDetail {
    generated_lyrics?: string   // 生成的歌词
    music_style?: string        // 音乐风格
    music_title?: string        // 音乐名称
    text?: string               // 配文/提示词
}

export interface LyricsTimelineLine {
    start_ms: number
    end_ms: number
    text: string
}

export interface LyricsTimelineSection {
    label: string
    start_ms: number
    end_ms: number
}

export interface LyricsTimelineData {
    text?: string
    language?: string
    duration_ms?: number
    lrc?: string
    lines?: LyricsTimelineLine[]
    sections?: LyricsTimelineSection[]
}

// 批量结果项
export interface BatchItemVO {
    fail_reason?: string
    model?: string
    results?: string[]
}

// 创作历史记录项
export interface CreationRecord {
    id: number
    name: string
    product_id: number
    prompts: string[]
    result_type: 'TEXT' | 'IMAGE' | 'VIDEO' | 'AUDIO'
    results: string[]
    created_at: number
    updated_at?: number
    status?: 'GENERATING' | 'SUCCESS' | 'PARTIAL_SUCCESS' | 'CANCELED' | 'FAILED'
    category?: 'IMAGE' | 'VIDEO' | 'TOOL' | 'MUSIC'
    type?: string
    type_name?: string
    music?: MusicHistoryDetail  // 音乐详情
    batch_id?: number
    batch_items?: BatchItemVO[]
    failed_items?: BatchItemVO[]
    expect_count?: number
    model?: string
    request_params?: Record<string, any> | string
    template_name?: string
    team_id?: string
    brand_id?: number
    show_failed?: boolean
    sora_video_id?: string
    has_like?: boolean  // 是否已点赞
    likes_num?: number  // 点赞数量
    result_list?: any[]  // 批量生成结果列表
    other_result_list?: any[]  // 其他结果列表
    failed_count?: number  // 失败数量
    lyrics_lines?: LyricsTimelineData
    /** 为 true 时该条为积分锁定记录，侧栏展示锁定大卡；此时本条 `id` 即 workflow 锁定记录 id（与 DELETE locked_record 一致） */
    locked_record?: boolean
    /** 兼容：若后端单独返回锁定单 id（一般与 `id` 相同） */
    locked_record_id?: number
    /** 锁定单所需积分（与锁定 query 的 credits_needed 对齐） */
    credits_needed?: number
    record_type?: string
    record_type_name?: string
    create_type?: string
    workflow_type?: string
    workflowType?: string
    credit_workflow_key?: string
}

// 分页响应 - 匹配实际 API 返回结构
export interface PageResponse<T> {
    content: T[]
    total: number
    page_size: number // API 返回的是 page_size
    total_page: number // API 返回的是 total_page
}

/**
 * @description: 获取创作历史记录
 * @param {object} params - 查询参数
 * @param {number} params.page - 页码
 * @param {number} params.size - 每页大小
 * @param {string} params.resultType - 结果类型（IMAGE/VIDEO/TEXT/AUDIO）
 * @param {string} params.category - 分类（IMAGE/VIDEO/TOOL/MUSIC）
 * @param {number} params.productId - 产品ID
 * @param {string} params.name - 名称搜索
 * @param {number} params.startTime - 开始时间戳（毫秒）
 * @param {number} params.endTime - 结束时间戳（毫秒）
 * @return {Promise}
 */
export const getCreationHistoryApi = (
    params: {
        page: number
        size: number
        resultType?: 'IMAGE' | 'VIDEO' | 'TEXT' | 'AUDIO'
        productId?: number
        name?: string
        startTime?: number
        endTime?: number
        category?: 'IMAGE' | 'VIDEO' | 'TOOL' | 'MUSIC' | ''
    },
    options?: { skipProductId?: boolean }
): Promise<{ status: number; data: PageResponse<CreationRecord>; message?: string }> => {
    return http.get<PageResponse<CreationRecord>>(
        '/backend/auth/ad-creative-history-record/page',
        params,
        options
    )
}

interface CreationShareLinkResponse {
    desc: string
    message: string
    status: number
    success: boolean
}

/**
 * @description: 生成分享链接id
 * @param {object} data - 请求参数
 * @param {number} data.history_id - 创作历史记录ID
 * @param {string} data.resource_url - 资源URL
 * @return {*}
 */
export const getCreationShareLinkApi = (data: {
    history_id: number
    resource_url: string
}) => {
    return http.post<CreationShareLinkResponse>(
        '/api/v1/auth/creative/share',
        data
    )
}
